import { useCallback, useEffect } from 'react';

import { DeliveryType } from '@/domain/models/Order';
import { SalesReport } from '@/domain/models/SalesReport';
import { useSalesReportState } from '@/store/sales_aggregation';

import { useSalesReport } from './useSalesReport';

export interface SalesFilter {
  from: Date;
  to: Date;
  masterBrandIDs: string[];
  masterBrandNames: string[];
  masterStoreIDs: string[];
  deliveryServiceIds: string[];
  deliveryTypes: DeliveryType[];
  authStoreIds: string[];
}

interface FilteringOrders {
  salesReport: SalesReport;
  isLoading: boolean;
  error: Error | null;
  filter: SalesFilter;
  setFilter: (filter: SalesFilter, submit?: boolean) => void;
}

export const useSalesReportFilter = (): FilteringOrders => {
  const {
    from,
    to,
    masterBrandIDs,
    masterBrandNames,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
    shouldFetch,
    setFrom,
    setTo,
    setMasterBrandIDs,
    setMasterBrandNames,
    setMasterStoreIDs,
    setDeliveryServiceIds,
    setDeliveryTypes,
    setAuthStoreIds,
    setShouldFetch,
  } = useSalesReportState();

  const { salesReport, isLoading, error } = useSalesReport(
    {
      startDate: from,
      endDate: to,
      masterBrandIDs,
      masterBrandNames,
      masterStoreIDs,
      deliveryServiceIds,
      deliveryTypes,
      authStoreIds,
    },
    shouldFetch // Controls whether query executes
  );

  useEffect(() => {
    if (shouldFetch && !isLoading) {
      setShouldFetch(false);
    }
  }, [shouldFetch, isLoading, setShouldFetch]);

  const filter: SalesFilter = {
    from,
    to,
    masterBrandIDs,
    masterBrandNames,
    masterStoreIDs,
    deliveryServiceIds,
    deliveryTypes,
    authStoreIds,
  };

  const setFilter = useCallback(
    (filter: SalesFilter, submit = false) => {
      setFrom(filter.from);
      setTo(filter.to);
      setMasterBrandIDs(filter.masterBrandIDs);
      setMasterBrandNames(filter.masterBrandNames);
      setMasterStoreIDs(filter.masterStoreIDs);
      setDeliveryServiceIds(filter.deliveryServiceIds);
      setDeliveryTypes(filter.deliveryTypes);
      setAuthStoreIds(filter.authStoreIds);

      if (submit) {
        setShouldFetch(true);
      }
    },
    [
      setFrom,
      setTo,
      setMasterBrandIDs,
      setMasterBrandNames,
      setMasterStoreIDs,
      setDeliveryServiceIds,
      setDeliveryTypes,
      setAuthStoreIds,
      setShouldFetch,
    ]
  );

  return {
    salesReport,
    isLoading,
    error,
    filter,
    setFilter,
  };
};
